import { Booking, JobCategory } from "@/lib/services/bookings";
import { formatETA } from "@/lib/utils/map-utils";

export const formatCollectionDate = (dateStr: string): string => {
  const date = new Date(dateStr);

  return `${date.toLocaleDateString()} at ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

export const getJobCategoryBadge = (
  category?: JobCategory | null,
  scheduledAt?: string | null,
) => {
  switch (category) {
    case "live":
      return {
        label: "Live / On-demand",
        icon: "flash" as const,
        color: "#DC2626",
        backgroundColor: "#FEE2E2",
        description: "Immediate action required",
      };
    case "scheduled":
      return {
        label: "Scheduled",
        icon: "calendar" as const,
        color: "#2563EB",
        backgroundColor: "#DBEAFE",
        description: scheduledAt
          ? `Scheduled for ${formatCollectionDate(scheduledAt)}`
          : "Scheduled for a future date",
      };
    case "quote_only":
      return {
        label: "Quote only",
        icon: "pricetag" as const,
        color: "#7C3AED",
        backgroundColor: "#EDE9FE",
        description: "Customer is requesting a price",
      };
    default:
      return null;
  }
};

export const formatBookingDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

export const getServiceSubtitle = (item: Booking) => {
  const notes = item.notes || item.questions?.[0]?.notes;
  if (notes) {
    return `${item.service_type_label} (${notes})`;
  }
  return item.service_type_label;
};

export const getStatusBadge = (status: string) => {
  switch (status) {
    case "completed":
      return {
        label: "Completed",
        color: "#16A34A",
        backgroundColor: "#DCFCE7",
      };
    case "delivered":
      return {
        label: "Delivered",
        color: "#16A34A",
        backgroundColor: "#DCFCE7",
      };
    case "cancelled":
      return {
        label: "Canceled",
        color: "#DC2626",
        backgroundColor: "#FEE2E2",
      };
    case "pending":
      return {
        label: "Pending",
        color: "#EA580C",
        backgroundColor: "#FFEDD5",
      };
    case "quoted":
      return {
        label: "Quoted",
        color: "#5856D6",
        backgroundColor: "#E5E7EB",
      };
    case "accepted":
      return {
        label: "Accepted",
        color: "#007AFF",
        backgroundColor: "#E5E7EB",
      };
    case "scheduled":
      return {
        label: "Scheduled",
        color: "#2563EB",
        backgroundColor: "#DBEAFE",
      };
    case "arrived":
      return {
        label: "Arrived",
        color: "#0FB9B1",
        backgroundColor: "#E5E7EB",
      };
    case "pickup_inspection":
      return {
        label: "Pickup Inspection",
        color: "#5856D6",
        backgroundColor: "#E5E7EB",
      };
    case "in_progress":
      return {
        label: "In Progress",
        color: "#34C759",
        backgroundColor: "#E5E7EB",
      };
    case "delivery_inspection":
      return {
        label: "Delivery Inspection",
        color: "#5856D6",
        backgroundColor: "#E5E7EB",
      };
    default:
      return {
        label: status,
        color: "#EA580C",
        backgroundColor: "#FFEDD5",
      };
  }
};

export const getBookingElapsedTime = (booking: Booking) => {
  const endDate =
    booking.completed_at ||
    booking.cancelled_at ||
    booking.updated_at ||
    new Date().toISOString();
  const diffMinutes =
    (new Date(endDate).getTime() - new Date(booking.created_at).getTime()) /
    60000;
  return formatETA(Math.max(diffMinutes, 0));
};
